import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Empty,
  Flex,
  Select,
  Switch,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Download, RotateCw, Trash2 } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AuditLogArchiveStatusEnum,
  AuditLogArchiveFormatEnum,
  organizationsAuditArchivesBulkDeleteDestroyMutation,
  organizationsAuditArchivesDestroyMutation,
  organizationsAuditArchivesDownloadRetrieve,
  organizationsAuditArchivesListOptions,
  organizationsAuditArchivesListQueryKey,
  type AuditLogArchive
} from 'src/api/main'
import {
  ActiveFilterTags,
  BadgeText,
  BulkActionsBar,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle,
  UserCell
} from 'src/components'
import type { BulkAction as BulkActionBarAction } from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { FilterLabelsProvider } from 'src/context'
import {
  useHasWorkspacePermissions,
  useQueryParams,
  useQueryUpdate
} from 'src/hooks'
import { defaultFilters, WorkspacePermissionCode } from 'src/utils'
import type { ActiveFilterConfig } from 'src/utils'

const { Text } = Typography

type Props = {
  workspaceId: number
  idValid: boolean
  /**
   * When set, the list is hard-scoped to this application. The "include
   * app-scoped" toggle is hidden — the scope is implied by where the panel is
   * embedded (i.e. inside App detail → Audit → Exports).
   */
  applicationId?: number
  /** Rendered as the left slot of the toolbar's top row (view switcher). */
  viewSelector: ReactNode
  /** Optional heading rendered in the top-left of the toolbar. */
  header?: ReactNode
}

type StatusFilter = AuditLogArchiveStatusEnum | 'all'

const STATUS_VALUES = [
  'all',
  AuditLogArchiveStatusEnum.PENDING,
  AuditLogArchiveStatusEnum.PROCESSING,
  AuditLogArchiveStatusEnum.READY,
  AuditLogArchiveStatusEnum.FAILED
] as const satisfies readonly StatusFilter[]

const STATUS_TAG_COLOR: Record<AuditLogArchiveStatusEnum, string> = {
  [AuditLogArchiveStatusEnum.PENDING]: 'gold',
  [AuditLogArchiveStatusEnum.PROCESSING]: 'blue',
  [AuditLogArchiveStatusEnum.READY]: 'green',
  [AuditLogArchiveStatusEnum.FAILED]: 'red'
}

const FORMAT_LABEL: Record<AuditLogArchiveFormatEnum, string> = {
  [AuditLogArchiveFormatEnum.CSV]: 'CSV',
  [AuditLogArchiveFormatEnum.XLSX]: 'XLSX',
  [AuditLogArchiveFormatEnum.JSONL]: 'JSONL'
}

const POLL_INTERVAL_MS = 5000

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`

  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const extractFilenameFromUrl = (rawUrl: string): string | null => {
  try {
    const parsed = new URL(rawUrl)
    const last = parsed.pathname.split('/').filter(Boolean).pop() ?? ''

    return last ? decodeURIComponent(last) : null
  } catch {
    return null
  }
}

const AuditExportsPanelBase = ({
  workspaceId,
  idValid,
  applicationId: applicationIdProp,
  viewSelector,
  header
}: Props) => {
  const { t } = useTranslation()
  const { message, modal } = AntdApp.useApp()
  const queryClient = useQueryClient()

  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  const hasApplicationScope = applicationIdProp != null
  const { all: allParam, ...listParams } = params
  const statusFilter = (params.status as StatusFilter | undefined) ?? 'all'
  const filterTagItems: ActiveFilterConfig[] = [
    {
      type: 'select',
      key: 'status',
      label: t('workspace.auditPage.exports.filters.allStatuses')
    }
  ]
  const includeAppScoped = allParam === 'true' || allParam === true

  const listQueryOptions = organizationsAuditArchivesListOptions({
    path: { organization_id: workspaceId },
    query: {
      ...defaultFilters,
      ...listParams,
      type: 'export',
      ...(hasApplicationScope
        ? { application: applicationIdProp }
        : includeAppScoped
          ? { all: true }
          : {})
    }
  })

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    ...listQueryOptions,
    enabled: idValid,
    placeholderData: (previousData) => previousData,
    // Always re-fetch when the panel mounts (i.e. when the user switches to
    // the Exports tab) — `invalidateQueries` only touches keys that are
    // already in the cache, so a first-time visit after requesting an export
    // would otherwise show stale data.
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      const rows = query.state.data?.results ?? []
      const hasInFlight = rows.some(
        (row) =>
          row.status === AuditLogArchiveStatusEnum.PENDING ||
          row.status === AuditLogArchiveStatusEnum.PROCESSING
      )

      return hasInFlight ? POLL_INTERVAL_MS : false
    }
  })

  const total = data?.count ?? 0

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: organizationsAuditArchivesListQueryKey({
        path: { organization_id: workspaceId }
      })
    })

  const destroy = useMutation({
    ...organizationsAuditArchivesDestroyMutation(),
    onSuccess: () => {
      message.success(t('workspace.auditPage.exports.deleteSuccess'))
      void invalidateList()
    },
    onError: () => message.error(t('workspace.auditPage.exports.deleteFailed'))
  })

  const bulkDestroy = useMutation({
    ...organizationsAuditArchivesBulkDeleteDestroyMutation(),
    onSuccess: () => {
      message.success(t('workspace.auditPage.exports.bulkDeleteSuccess'))
      setSelectedIds([])
      void invalidateList()
    },
    onError: () => message.error(t('workspace.auditPage.exports.deleteFailed'))
  })

  const canExportAudit = useHasWorkspacePermissions(
    WorkspacePermissionCode.AUDIT_EXPORT
  )

  const handleDelete = (archive: AuditLogArchive) => {
    modal.confirm({
      title: t('workspace.auditPage.exports.confirmDeleteTitle'),
      content: t('workspace.auditPage.exports.confirmDeleteContent'),
      okText: t('workspace.auditPage.exports.actions.delete'),
      okButtonProps: { danger: true },
      onOk: async () => {
        await destroy.mutateAsync({
          path: { organization_id: workspaceId, id: archive.id }
        })
      }
    })
  }

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return

    modal.confirm({
      title: t('workspace.auditPage.exports.confirmBulkDeleteTitle'),
      content: t('workspace.auditPage.exports.confirmBulkDeleteContent', {
        count: selectedIds.length
      }),
      okText: t('workspace.auditPage.exports.actions.delete'),
      okButtonProps: { danger: true },
      onOk: async () => {
        await bulkDestroy.mutateAsync({
          path: { organization_id: workspaceId },
          query: { ids: selectedIds }
        })
      }
    })
  }

  const handleDownload = async (archive: AuditLogArchive) => {
    setDownloadingId(archive.id)
    try {
      const response = await organizationsAuditArchivesDownloadRetrieve({
        path: { organization_id: workspaceId, id: archive.id },
        throwOnError: true
      })
      const payload = response.data

      if (!payload?.url) {
        message.error(t('workspace.auditPage.exports.downloadFailed'))

        return
      }

      // Cross-origin `href` silently drops the `download` attribute, so we
      // fetch the blob and re-serve it from a same-origin object URL to force
      // a filename-preserving download.
      const fileResponse = await fetch(payload.url)

      if (!fileResponse.ok) {
        throw new Error(`storage fetch ${fileResponse.status}`)
      }

      const blob = await fileResponse.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const filename =
        extractFilenameFromUrl(payload.url) ??
        `audit-export-${archive.id}.${archive.format}`

      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch {
      message.error(t('workspace.auditPage.exports.downloadFailed'))
    } finally {
      setDownloadingId(null)
    }
  }

  const columns: ColumnsType<AuditLogArchive> = useMemo(
    () => [
      {
        title: t('workspace.auditPage.exports.columns.period'),
        key: 'period',
        fixed: 'left',
        width: 200,
        render: (_v, record) => (
          <Flex align="center" gap={6} style={{ whiteSpace: 'nowrap' }}>
            <DateTimeCell
              value={record.period_start}
              variant="date"
              style={{ fontSize: 12 }}
            />
            <span style={{ color: 'var(--ant-color-text-tertiary)' }}>→</span>
            <DateTimeCell
              value={record.period_end}
              variant="date"
              style={{ fontSize: 12 }}
            />
          </Flex>
        )
      },
      {
        title: t('workspace.auditPage.exports.columns.format'),
        dataIndex: 'format',
        key: 'format',
        width: 80,
        render: (format: AuditLogArchiveFormatEnum) => (
          <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
            {FORMAT_LABEL[format]}
          </Tag>
        )
      },
      {
        title: t('workspace.auditPage.exports.columns.status'),
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (status: AuditLogArchiveStatusEnum, record) => {
          const tag = (
            <Tag
              color={STATUS_TAG_COLOR[status]}
              style={{ marginInlineEnd: 0, fontSize: 10 }}
            >
              {t(`workspace.auditPage.exports.status.${status}`)}
            </Tag>
          )

          if (
            status === AuditLogArchiveStatusEnum.FAILED &&
            record.error_message
          ) {
            return (
              <Tooltip placement="top" title={record.error_message}>
                {tag}
              </Tooltip>
            )
          }

          return tag
        }
      },
      {
        title: t('workspace.auditPage.exports.columns.requestedBy'),
        key: 'requestedBy',
        width: 240,
        render: (_v, record) =>
          record.requested_by ? (
            <UserCell
              firstName={record.requested_by.first_name}
              lastName={record.requested_by.last_name}
              email={record.requested_by.email}
              photoUrl={record.requested_by.photo?.file}
              size="sm"
            />
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('workspace.auditPage.exports.columns.fileSize'),
        dataIndex: 'file_size',
        key: 'fileSize',
        width: 100,
        align: 'right',
        render: (size: number | null) => (
          <Text style={{ fontSize: 12 }}>{formatBytes(size)}</Text>
        )
      },
      {
        title: t('workspace.auditPage.exports.columns.createdAt'),
        dataIndex: 'date_created',
        key: 'createdAt',
        width: 150,
        render: (value: string) => (
          <DateTimeCell
            value={value}
            variant="relative"
            style={{ fontSize: 12 }}
          />
        )
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_v, record) =>
          !canExportAudit ? null : (
            <Flex gap={4}>
              <Tooltip
                title={t('workspace.auditPage.exports.actions.download')}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<Download size={14} />}
                  disabled={
                    record.status !== AuditLogArchiveStatusEnum.READY ||
                    downloadingId === record.id
                  }
                  loading={downloadingId === record.id}
                  onClick={() => void handleDownload(record)}
                  aria-label={t('workspace.auditPage.exports.actions.download')}
                />
              </Tooltip>
              <Tooltip title={t('workspace.auditPage.exports.actions.delete')}>
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<Trash2 size={14} />}
                  onClick={() => handleDelete(record)}
                  aria-label={t('workspace.auditPage.exports.actions.delete')}
                />
              </Tooltip>
            </Flex>
          )
      }
    ],
    [t, downloadingId]
  )

  const bulkActions: BulkActionBarAction[] = [
    {
      title: t('workspace.auditPage.exports.bulk.delete'),
      buttonProps: {
        danger: true,
        icon: <Trash2 />,
        loading: bulkDestroy.isPending,
        onClick: handleBulkDelete
      }
    }
  ]

  if (isError) {
    return <QueryErrorAlert onRetry={() => void refetch()} />
  }

  return (
    <>
      <ProTableContainer<AuditLogArchive>
        showSearch={false}
        tableIdentifier="workspace-audit-exports"
        data={data}
        columns={columns}
        loading={{ spinning: isLoading, delay: 200 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys as number[])
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('workspace.auditPage.exports.empty')}
            />
          )
        }}
        cardTitle={header ?? viewSelector}
        cardExtra={
          <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
            {header ? viewSelector : null}
            <Text
              type="secondary"
              style={{ fontSize: 11, whiteSpace: 'nowrap' }}
            >
              {t('workspace.auditPage.exports.total', { count: total })}
            </Text>
            <Tooltip title={t('workspace.auditPage.exports.refresh')}>
              <Button
                size="small"
                type="text"
                icon={<RotateCw size={14} />}
                onClick={() => void refetch()}
                loading={isFetching}
                aria-label={t('workspace.auditPage.exports.refresh')}
              />
            </Tooltip>
          </Flex>
        }
        leftChildren={
          <>
            <Select<StatusFilter>
              size="small"
              allowClear
              placeholder={t('workspace.auditPage.exports.filters.allStatuses')}
              value={statusFilter === 'all' ? undefined : statusFilter}
              onChange={(value) =>
                updateQuery({
                  ...params,
                  page: defaultFilters.page,
                  status: value ?? null
                })
              }
              style={{ width: FILTER_CONTROL_WIDTH }}
              options={STATUS_VALUES.filter((v) => v !== 'all').map(
                (status) => ({
                  value: status,
                  label: (
                    <BadgeText
                      text={t(`workspace.auditPage.exports.status.${status}`)}
                      color={
                        STATUS_TAG_COLOR[status as AuditLogArchiveStatusEnum]
                      }
                    />
                  )
                })
              )}
            />
            {!hasApplicationScope ? (
              <Flex align="center" gap={6}>
                <Switch
                  size="small"
                  checked={includeAppScoped}
                  onChange={(checked) =>
                    updateQuery({
                      ...params,
                      page: defaultFilters.page,
                      all: checked ? true : null
                    })
                  }
                />
                <Text style={{ fontSize: 12 }}>
                  {t('workspace.auditPage.exports.filters.includeAppScoped')}
                </Text>
              </Flex>
            ) : null}

            <Flex flex="1 0 100%">
              <ActiveFilterTags
                items={filterTagItems}
                onClearAll={
                  statusFilter === 'all'
                    ? undefined
                    : () =>
                        updateQuery({
                          ...params,
                          page: defaultFilters.page,
                          status: null
                        })
                }
              />
            </Flex>
          </>
        }
      />

      <BulkActionsBar
        count={selectedIds.length}
        actions={bulkActions}
        onDeselect={() => setSelectedIds([])}
      />
    </>
  )
}

export const AuditExportsPanel = (props: Props) => (
  <FilterLabelsProvider>
    <AuditExportsPanelBase {...props} />
  </FilterLabelsProvider>
)
